import { useState, useMemo } from "react";
import { useListTasks, useListInstances, Task, RecurrencePattern } from "@/lib/api/checklist";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getDateRangeForRecurrence } from "@/lib/utils/recurrence";
import { MonthlyWeeklyBreakdownView } from "./MonthlyWeeklyBreakdownView";
import { Skeleton } from "@/components/ui/skeleton";

interface MonthlyBreakdownCardProps {
  searchQuery?: string;
}

export function MonthlyBreakdownCard({ searchQuery = '' }: MonthlyBreakdownCardProps) {
  const [lookbackMonths, setLookbackMonths] = useState(6);

  // Fetch all tasks with search filter
  const tasksResult = useListTasks({
    is_active: true,
    limit: 100000,
    search: searchQuery || undefined,
  });

  // Calculate date range for monthly
  const dateRange = useMemo(() => {
    return getDateRangeForRecurrence(RecurrencePattern.MONTHLY, lookbackMonths);
  }, [lookbackMonths]);

  // Fetch all instances within the date range
  const instancesResult = useListInstances({
    due_date_from: dateRange.start.toISOString(),
    due_date_to: dateRange.end.toISOString(),
    limit: 100000,
    search: searchQuery || undefined,
  });

  const tasks = tasksResult?.data?.data?.tasks || [];
  const instances = instancesResult?.data?.data?.instances || [];
  const isLoading = tasksResult.isLoading || instancesResult.isLoading;

  // Filter tasks by monthly recurrence pattern
  const filteredTasks = useMemo(() => {
    return tasks.filter((task: Task) => task.recurrence_pattern === RecurrencePattern.MONTHLY);
  }, [tasks]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Monthly Tasks</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-2">
          <Label htmlFor="lookback-months" className="whitespace-nowrap">
            Show last:
          </Label>
          <Input
            id="lookback-months"
            type="number"
            min="3"
            max="24"
            value={lookbackMonths}
            onChange={(e) => setLookbackMonths(parseInt(e.target.value) || 3)}
            className="w-20"
          />
          <span className="text-sm text-muted-foreground">months</span>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No tasks found with monthly recurrence
          </div>
        ) : (
          <MonthlyWeeklyBreakdownView
            tasks={filteredTasks}
            instances={instances}
            lookbackPeriods={lookbackMonths - 1}
            pattern={RecurrencePattern.MONTHLY}
          />
        )}
      </CardContent>
    </Card>
  );
}
